import { useState } from 'react'
import { Copy, Download } from 'lucide-react'
import type { SalesminerOpportunity } from '@/types/serializers'
import { Card, CardContent, CardHeader } from "@/components/ui/card.tsx";

interface ActionsTabProps {
    opportunity: SalesminerOpportunity
}

export default function ActionsTab({opportunity}: ActionsTabProps) {
    const [copySubjectSuccess, setCopySubjectSuccess] = useState(false)
    const [copyMessageSuccess, setCopyMessageSuccess] = useState(false)

    // Function to process template variables
    const processTemplate = (template: string) => {
        if(!template) {
            return null
        }
        // TODO: Get actual user data from context/props
        const userName = 'John Doe' // Placeholder
        const companyName = 'Acme Corporation' // Placeholder

        return template
            .replace(/\{\{user\.name\}\}/g, userName)
            .replace(/\{\{user\.company\}\}/g, companyName)
            .replace(/\{\{contact\.name\}\}/g, '[Contact Name]') // Placeholder for contact name
    }
    // Process templates to replace variables
    const emailData = {
        subject: processTemplate(opportunity.email_template.subject),
        message: processTemplate(opportunity.email_template.body)
    }

    const handleCopyToClipboard = async (text: string, type: 'subject' | 'message') => {
        try {
            await navigator.clipboard.writeText(text)
            if (type === 'subject') {
                setCopySubjectSuccess(true)
                setTimeout(() => setCopySubjectSuccess(false), 2000)
            } else {
                setCopyMessageSuccess(true)
                setTimeout(() => setCopyMessageSuccess(false), 2000)
            }
        } catch {
            // Fallback for older browsers
            try {
                const textArea = document.createElement('textarea')
                textArea.value = text
                document.body.appendChild(textArea)
                textArea.select()
                document.execCommand('copy')
                document.body.removeChild(textArea)

                if (type === 'subject') {
                    setCopySubjectSuccess(true)
                    setTimeout(() => setCopySubjectSuccess(false), 2000)
                } else {
                    setCopyMessageSuccess(true)
                    setTimeout(() => setCopyMessageSuccess(false), 2000)
                }
            } catch {
                console.error('Failed to copy text to clipboard')
            }
        }
    }

    return (
        <div className="space-y-8">
            {/* Email Template Section */}
            <Card
            >
                <CardHeader>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-neutral-50 mb-6">Email Template</h3>
                </CardHeader>
                <CardContent>

                    {/* Subject Line */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Subject</label>
                            <button
                                onClick={() => handleCopyToClipboard(emailData.subject, 'subject')}
                                className="flex items-center space-x-2 px-3 py-1 text-sm bg-slate-100 dark:bg-dark-bg-600 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-dark-bg-500 transition-colors"
                            >
                                <Copy className="w-4 h-4"/>
                                <span>{copySubjectSuccess ? 'Copied!' : 'Copy'}</span>
                            </button>
                        </div>
                        <div
                            className="p-3  bg-input text-foreground border-border rounded-md">
                            <p className="text-sm text-slate-800 dark:text-slate-200">
                                {emailData.subject}
                            </p>
                        </div>
                    </div>

                    {/* Message Body */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Message</label>
                            <button
                                onClick={() => handleCopyToClipboard(emailData.message, 'message')}
                                className="flex items-center space-x-2 px-3 py-1 text-sm bg-slate-100 dark:bg-dark-bg-600 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-dark-bg-500 transition-colors"
                            >
                                <Copy className="w-4 h-4"/>
                                <span>{copyMessageSuccess ? 'Copied!' : 'Copy'}</span>
                            </button>
                        </div>
                        <textarea
                            className="w-full h-64 p-4 bg-input text-foreground border-border rounded-md"
                            value={emailData.message}
                            readOnly
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Export Section */}
            <Card>
                <CardHeader>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-neutral-50 mb-6">Export
                        opportunity</h3>
                </CardHeader>
                <CardContent>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <button
                            disabled
                            className="flex items-center justify-center space-x-2 w-full bg-slate-50 dark:bg-dark-bg-600 border border-slate-200 dark:border-dark-border-light rounded-lg p-3 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                        >
                            <Download className="w-4 h-4"/>
                            <span>.CSV File</span>
                        </button>

                        <button
                            disabled
                            className="flex items-center justify-center space-x-2 w-full bg-slate-50 dark:bg-dark-bg-600 border border-slate-200 dark:border-dark-border-light rounded-lg p-3 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                        >
                            <Download className="w-4 h-4"/>
                            <span>SalesForce</span>
                        </button>

                        <button
                            disabled
                            className="flex items-center justify-center space-x-2 w-full bg-slate-50 dark:bg-dark-bg-600 border border-slate-200 dark:border-dark-border-light rounded-lg p-3 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                        >
                            <Download className="w-4 h-4"/>
                            <span>Hubspot</span>
                        </button>
                    </div>

                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-4 text-center">
                        Export functionality will be soon available
                    </p>
                </CardContent>

            </Card>
        </div>
    )
}